import { useState, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { FormField, FormToggle } from '@/shared/components'
import { EtudiantAvatar } from './etudiant-avatar'
import type { Etudiant } from '@/shared/types/etudiant'
import { compressImage } from '@/lib/image'
import { useQuery } from '@tanstack/react-query'
import { formateursAdminApi } from '@/features/admin/api/formateurs-admin.api'

const etudiantSchema = z.object({
  prenom: z.string().min(1, 'Le prénom est obligatoire'),
  nom: z.string().min(1, 'Le nom est obligatoire'),
  email: z.string().email('Email invalide'),
  telephone: z.string().optional().or(z.literal('')),
  consentementSilencieux: z.boolean(),
  consentementConsultation: z.boolean(),
  consentementModificationPlanning: z.boolean(),
  nbMotsLangue: z.number(),
  heureNotificationScan: z.number().min(360).max(1320),
  nbJoursAvantVacancesNotif: z.number().min(0).max(30),
  typeMemoire: z.string().optional().or(z.literal('')),
  formateurId: z.number().optional(),
})

type EtudiantFormValues = z.infer<typeof etudiantSchema>

interface EtudiantFormProps {
  etudiant?: Etudiant
  onSubmit: (data: EtudiantFormValues, photoFile?: File, photoRemoved?: boolean) => void
  isPending: boolean
}

export function EtudiantForm({ etudiant, onSubmit, isPending }: EtudiantFormProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const isEdit = !!etudiant

  const { data: formateurs } = useQuery({
    queryKey: ['admin', 'formateurs'],
    queryFn: formateursAdminApi.getAll,
  })

  const [photoFile, setPhotoFile] = useState<File | null>(null)
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const [photoRemoved, setPhotoRemoved] = useState(false)

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm<EtudiantFormValues>({
    resolver: zodResolver(etudiantSchema),
    defaultValues: {
      prenom: '',
      nom: '',
      email: '',
      telephone: '',
      consentementSilencieux: false,
      consentementConsultation: false,
      consentementModificationPlanning: false,
      nbMotsLangue: 5,
      heureNotificationScan: 1020,
      nbJoursAvantVacancesNotif: 0,
      typeMemoire: '',
      formateurId: undefined,
    },
  })

  useEffect(() => {
    if (etudiant) {
      setValue('prenom', etudiant.prenom)
      setValue('nom', etudiant.nom)
      setValue('email', etudiant.email)
      setValue('telephone', etudiant.telephone || '')
      setValue('consentementSilencieux', etudiant.consentementSilencieux)
      setValue('consentementConsultation', etudiant.consentementConsultation)
      setValue('consentementModificationPlanning', etudiant.consentementModificationPlanning)
      setValue('nbMotsLangue', etudiant.nbMotsLangue)
      setValue('heureNotificationScan', etudiant.heureNotificationScan)
      setValue('nbJoursAvantVacancesNotif', etudiant.nbJoursAvantVacancesNotif)
      setValue('typeMemoire', etudiant.typeMemoire || '')
      setValue('formateurId', etudiant.formateurId ?? undefined)
    }
  }, [etudiant, setValue])

  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl)
    }
  }, [previewUrl])

  const handlePhotoChange = async (file: File) => {
    const compressed = await compressImage(file)
    setPhotoFile(compressed)
    setPhotoRemoved(false)
    if (previewUrl) URL.revokeObjectURL(previewUrl)
    setPreviewUrl(URL.createObjectURL(compressed))
  }

  const handlePhotoRemove = () => {
    setPhotoFile(null)
    setPhotoRemoved(true)
    if (previewUrl) URL.revokeObjectURL(previewUrl)
    setPreviewUrl(null)
  }

  const handleFormSubmit = (values: EtudiantFormValues) => {
    onSubmit(values, photoFile ?? undefined, photoRemoved)
  }

  const inputClass =
    'h-10 w-full rounded-lg border border-neutral-300 bg-neutral-50 px-3 text-sm transition-colors hover:border-neutral-400 focus:border-primary focus:bg-white focus:outline-none focus:ring-2 focus:ring-primary/25'

  const readOnlyClass =
    'h-10 w-full rounded-lg border border-neutral-200 bg-neutral-100 px-3 text-sm text-neutral-500 cursor-not-allowed'

  const selectClass =
    'h-10 w-full rounded-lg border border-neutral-300 bg-neutral-50 px-3 text-sm transition-colors hover:border-neutral-400 focus:border-primary focus:bg-white focus:outline-none focus:ring-2 focus:ring-primary/25'

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-6">
      {/* ── Card: Identité ── */}
      <div className="rounded-xl border border-neutral-200 bg-white shadow-sm">
        <div className="border-b border-neutral-100 px-6 py-4">
          <h2 className="text-base font-semibold text-primary">
            {t('student.identity.informationTitle', "Identité de l'étudiant")}
          </h2>
        </div>
        <div className="space-y-5 p-6">
          {/* Avatar */}
          {isEdit && (
            <div className="flex justify-center pb-2">
              <EtudiantAvatar
                photoUrl={photoRemoved ? null : etudiant.photoUrl}
                prenom={etudiant.prenom}
                nom={etudiant.nom}
                editable
                previewUrl={previewUrl}
                onPhotoChange={handlePhotoChange}
                onPhotoRemove={handlePhotoRemove}
              />
            </div>
          )}

          {/* Edit mode: Email, Prénom, Nom, Téléphone */}
          {isEdit && (
            <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
              <FormField label={t('student.identity.email', 'Email')}>
                <input value={etudiant.email} readOnly className={readOnlyClass} />
              </FormField>
              <FormField
                label={t('student.identity.firstName', 'Prénom')}
                error={errors.prenom?.message}
                required
              >
                <input {...register('prenom')} className={inputClass} />
              </FormField>
              <FormField
                label={t('student.identity.lastName', 'Nom')}
                error={errors.nom?.message}
                required
              >
                <input {...register('nom')} className={inputClass} />
              </FormField>
              <FormField label={t('student.identity.phone', 'Téléphone')}>
                <input type="tel" {...register('telephone')} className={inputClass} />
              </FormField>
            </div>
          )}

          {/* Create mode: Email, Prénom, Nom, Téléphone */}
          {!isEdit && (
            <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
              <FormField
                label={t('student.identity.email', 'Email')}
                error={errors.email?.message}
                required
              >
                <input type="email" {...register('email')} className={inputClass} />
              </FormField>
              <FormField
                label={t('student.identity.firstName', 'Prénom')}
                error={errors.prenom?.message}
                required
              >
                <input {...register('prenom')} className={inputClass} />
              </FormField>
              <FormField
                label={t('student.identity.lastName', 'Nom')}
                error={errors.nom?.message}
                required
              >
                <input {...register('nom')} className={inputClass} />
              </FormField>
              <FormField label={t('student.identity.phone', 'Téléphone')}>
                <input type="tel" {...register('telephone')} className={inputClass} />
              </FormField>
            </div>
          )}
        </div>
      </div>

      {/* ── Card: Informations PEPS ── */}
      {isEdit && (
        <div className="rounded-xl border border-neutral-200 bg-white shadow-sm">
          <div className="border-b border-neutral-100 px-6 py-4">
            <h2 className="text-base font-semibold text-primary">
              Informations PEPS
            </h2>
          </div>
          <div className="space-y-6 p-6">
            {/* Parcours (read-only) */}
            {etudiant.parcours && etudiant.parcours.length > 0 && (
              <div>
                <h3 className="mb-3 text-sm font-semibold text-neutral-700">Parcours</h3>
                <div className="space-y-2">
                  {etudiant.parcours.map((p, i) => (
                    <div
                      key={i}
                      className="flex items-center gap-4 rounded-lg border border-neutral-100 bg-neutral-50 px-4 py-2.5 text-sm text-neutral-600"
                    >
                      {p.niveau && <span className="font-medium text-neutral-800">{p.niveau}</span>}
                      {p.etablissement && <span>{p.etablissement}</span>}
                      {p.dateAffectation && (
                        <span className="ml-auto text-xs text-neutral-400">{p.dateAffectation}</span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Nb mots langues + Type de mémoire + Heure notification */}
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              <FormField label={t('student.identity.wordsCount', 'Nombre de mots pour les langues')}>
                <select {...register('nbMotsLangue', { valueAsNumber: true })} className={selectClass}>
                  <option value={5}>5 mots</option>
                  <option value={10}>10 mots</option>
                  <option value={15}>15 mots</option>
                </select>
              </FormField>
              <FormField label="Type de mémoire, à dominante">
                <select {...register('typeMemoire')} className={selectClass}>
                  <option value="">— Non renseigné —</option>
                  <option value="KINESTHESIQUE">Kinesthésique</option>
                  <option value="VISUELLE">Visuelle</option>
                  <option value="AUDITIVE">Auditive</option>
                </select>
              </FormField>
              <FormField label={t('student.identity.scanNotificationHour', 'Heure notification SCAN')}>
                <div className="flex items-center gap-1.5">
                  <select
                    value={Math.floor(watch('heureNotificationScan') / 60)}
                    onChange={(e) => {
                      const minute = watch('heureNotificationScan') % 60
                      setValue('heureNotificationScan', Number(e.target.value) * 60 + minute)
                    }}
                    className={selectClass + ' !w-14 !min-w-0 !px-1.5 text-center'}
                  >
                    {Array.from({ length: 17 }, (_, i) => i + 6).map((h) => (
                      <option key={h} value={h}>{h}</option>
                    ))}
                  </select>
                  <span className="text-sm font-medium text-neutral-500">h</span>
                  <select
                    value={watch('heureNotificationScan') % 60}
                    onChange={(e) => {
                      const hour = Math.floor(watch('heureNotificationScan') / 60)
                      setValue('heureNotificationScan', hour * 60 + Number(e.target.value))
                    }}
                    className={selectClass + ' !w-14 !min-w-0 !px-1.5 text-center'}
                  >
                    <option value={0}>00</option>
                    <option value={30}>30</option>
                  </select>
                </div>
              </FormField>
              <FormField label={t('student.identity.vacancesNotifDays', 'Rappel vacances (jours avant)')}>
                <select
                  value={watch('nbJoursAvantVacancesNotif')}
                  onChange={(e) => setValue('nbJoursAvantVacancesNotif', Number(e.target.value))}
                  className={selectClass}
                >
                  {Array.from({ length: 6 }, (_, i) => i).map((n) => (
                    <option key={n} value={n}>{n === 0 ? 'Le jour même' : `${n} jour${n > 1 ? 's' : ''}`}</option>
                  ))}
                </select>
              </FormField>
            </div>

            {/* Consentements — en colonne */}
            <div>
              <h3 className="mb-3 text-sm font-semibold text-neutral-700">
                {t('student.identity.consentsTitle', 'Consentements')}
              </h3>
              <div className="flex flex-col gap-3">
                <FormToggle
                  label="Consentement Mode concentration"
                  checked={watch('consentementSilencieux')}
                  onChange={(v) => setValue('consentementSilencieux', v)}
                />
                <FormToggle
                  label={t('student.identity.consultationConsent', 'Consentement consultation')}
                  checked={watch('consentementConsultation')}
                  onChange={(v) => setValue('consentementConsultation', v)}
                />
                <FormToggle
                  label={t('student.identity.planningConsent', 'Consentement modification planning')}
                  checked={watch('consentementModificationPlanning')}
                  onChange={(v) => setValue('consentementModificationPlanning', v)}
                />
              </div>
            </div>

            {/* Formateur référent */}
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              <FormField label="Formateur référent">
                <select
                  value={watch('formateurId') ?? ''}
                  onChange={(e) => {
                    const val = e.target.value
                    setValue('formateurId', val ? Number(val) : undefined)
                  }}
                  className={selectClass}
                >
                  <option value="">— Aucun formateur —</option>
                  {formateurs?.map((f) => (
                    <option key={f.id} value={f.id!}>
                      {f.prenom} {f.nom}
                    </option>
                  ))}
                </select>
              </FormField>
            </div>
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="flex items-center justify-end gap-3 border-t border-neutral-200 pt-5">
        <button
          type="button"
          onClick={() => navigate('/etudiants')}
          className="rounded-lg border border-neutral-300 px-6 py-2.5 text-sm font-medium text-neutral-700 transition-colors hover:bg-neutral-50"
        >
          {t('student.identity.cancel', 'Annuler')}
        </button>
        <button
          type="submit"
          disabled={isPending}
          className="rounded-lg bg-primary px-8 py-2.5 text-sm font-medium text-white transition-colors hover:bg-primary/90 disabled:opacity-50"
        >
          {isPending
            ? t('student.identity.saving', 'Enregistrement...')
            : isEdit
              ? t('student.identity.save', 'Enregistrer')
              : t('student.identity.create', 'Créer')}
        </button>
      </div>
    </form>
  )
}
